/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type {
  DesktopRouteState,
  DesktopSessionState,
  DesktopSessionWindow,
} from '@poweralpha/common';
import type { DesktopApp } from './types';
import { isMaximizedByDefault } from './windowDefaults';
import {
  type DesktopState,
  type DesktopWindowState,
  getDefaultWindowMetrics,
  getFocusedWindowId,
  restoreHistory,
} from './windowSystem';
import { getWindowTabs, syncSelectedTab } from './windowTabs';

const DESKTOP_STATE_VERSION = 1;

const getPersistedWindowState = (
  windowState: DesktopWindowState,
  focusedWindowId: string | undefined,
): DesktopSessionWindow['state'] => {
  if (windowState.id === focusedWindowId && !windowState.isMinimized) {
    return 'focused';
  }
  if (windowState.isMinimized) {
    return 'minimized';
  }
  return 'open';
};

export const createDesktopStateFromSession = <
  RouteState extends DesktopRouteState = DesktopRouteState,
>(
  persistedState: DesktopSessionState<RouteState>,
  apps: readonly DesktopApp[],
): DesktopState<RouteState> => {
  const now = new Date().toISOString();
  const persistedByAppId = new Map(
    persistedState.windows.map((windowState) => [windowState.appId, windowState]),
  );
  const windows = apps
    .map((app, index) => {
      const persistedWindow = persistedByAppId.get(app.id);
      const metrics = persistedWindow?.bounds ?? getDefaultWindowMetrics(app.id, apps);
      const path = persistedWindow?.path ?? app.defaultPath;

      return {
        id: app.id,
        appId: app.id,
        title: persistedWindow?.title ?? app.label,
        path,
        history: restoreHistory(path, persistedWindow?.history),
        routeState: persistedWindow?.routeState,
        tabs: persistedWindow?.tabs?.map((tab) => ({
          ...tab,
          history: {
            ...restoreHistory(tab.path, tab.history),
            index:
              tab.history &&
              tab.historyIndex !== undefined &&
              tab.history[tab.historyIndex] === tab.path
                ? tab.historyIndex
                : restoreHistory(tab.path, tab.history).index,
          },
        })),
        selectedTabId: persistedWindow?.selectedTabId,
        restoreBounds: persistedWindow?.restoreBounds,
        x: metrics.x,
        y: metrics.y,
        width: metrics.width,
        height: metrics.height,
        zIndex: persistedWindow?.zIndex ?? index + 1,
        // Only open windows are saved, so a window missing from the session was closed.
        isOpen: persistedWindow !== undefined,
        isMinimized: persistedWindow?.state === 'minimized',
        isMaximized: persistedWindow?.isMaximized ?? isMaximizedByDefault(app.id),
        createdAt: persistedWindow?.createdAt ?? now,
        updatedAt: persistedWindow?.updatedAt ?? now,
      };
    })
    .map((windowState) => syncSelectedTab(windowState, {}));

  const highestZIndex = windows.reduce(
    (highest, windowState) => Math.max(highest, windowState.zIndex),
    apps.length,
  );

  return {
    windows,
    focusedWindowId: getFocusedWindowId(windows, persistedState.focusedWindowId ?? undefined),
    nextZIndex: highestZIndex + 1,
    tileLayout: persistedState.tileLayout,
  };
};

export const serializeDesktopState = <RouteState extends DesktopRouteState = DesktopRouteState>({
  desktopState,
  selectedProjectId,
  showWidgets,
}: {
  desktopState: DesktopState<RouteState>;
  selectedProjectId: string | null;
  showWidgets: boolean;
}): DesktopSessionState<RouteState> => {
  const now = new Date().toISOString();
  const focusedWindowId = getFocusedWindowId(desktopState.windows, desktopState.focusedWindowId);
  const windows: DesktopSessionWindow<RouteState>[] = desktopState.windows
    .filter((windowState) => windowState.isOpen)
    .map((windowState) => ({
      id: windowState.id,
      appId: windowState.appId,
      title: windowState.title,
      path: windowState.path,
      // Saved so Back works after a reload, and so the server keeps only these views' state.
      history: windowState.history.entries.map((entry) => entry.path),
      routeState: windowState.routeState,
      tabs: getWindowTabs(windowState).map((tab) => ({
        id: tab.id,
        path: tab.path,
        history: tab.history.entries.map(({ path }) => path),
        historyIndex: tab.history.index,
        routeState: tab.routeState,
      })),
      selectedTabId: windowState.selectedTabId ?? windowState.id,
      restoreBounds: windowState.restoreBounds,
      state: getPersistedWindowState(windowState, focusedWindowId),
      bounds: {
        x: windowState.x,
        y: windowState.y,
        width: windowState.width,
        height: windowState.height,
      },
      zIndex: windowState.zIndex,
      isMaximized: windowState.isMaximized,
      createdAt: windowState.createdAt,
      updatedAt: windowState.updatedAt,
    }));

  return {
    version: DESKTOP_STATE_VERSION,
    tileLayout: desktopState.tileLayout,
    windows,
    focusedWindowId: focusedWindowId ?? null,
    selectedProjectId,
    showWidgets,
    updatedAt: now,
  };
};
